import { useMemo } from 'react'
import {
  createStudioReviewDataClient,
  type AudienceKpi,
  type AudienceReviewData,
  type FanRecord,
  type StudioReviewDataClient,
  type TrafficSource,
} from '@/services/dashboard-api'
import { useDashboardSlice, useReloadToken } from './dashboard-data-state'
import type { DashboardSlice } from './dashboard-data-state'

export interface AudienceData {
  kpis: DashboardSlice<AudienceKpi | null>
  sources: DashboardSlice<TrafficSource[]>
  fans: DashboardSlice<FanRecord[]>
  isLoading: boolean
  reload: () => void
}

function projectSlice<T>(
  slice: DashboardSlice<AudienceReviewData>,
  select: (data: AudienceReviewData) => T,
): DashboardSlice<T> {
  return {
    ...slice,
    data: slice.data === null ? null : select(slice.data),
    evidenceRefs: [...slice.evidenceRefs],
  }
}

export function useAudienceData(injectedClient?: StudioReviewDataClient): AudienceData {
  const defaultClient = useMemo(() => createStudioReviewDataClient(), [])
  const client = injectedClient ?? defaultClient
  const [reloadToken, reload] = useReloadToken()
  const audience = useDashboardSlice(
    'audience',
    () => client.readAudience(),
    [client],
    reloadToken,
  )

  return {
    kpis: projectSlice(audience, (data) => data.kpis),
    sources: projectSlice(audience, (data) => data.sources),
    fans: projectSlice(audience, (data) => data.fans),
    isLoading: audience.status === 'loading',
    reload,
  }
}
